
import React, { useState, useMemo } from 'react';
import { 
  AppState, 
  DepartmentType, 
  UserRole, 
  Staff, 
  Shift 
} from './types';
import { 
  LOCATIONS,
  BRAND
} from './constants';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './screens/Dashboard';
import RotaPlanner from './screens/RotaPlanner';
import PeopleView from './screens/PeopleView';
import LeaveView from './screens/LeaveView';
import TimesheetView from './screens/TimesheetView';
import FileStorageView from './screens/FileStorageView';
import JobsView from './screens/JobsView';
import FormsView from './screens/FormsView';
import WarmSpacesView from './screens/WarmSpacesView';
import LocationSettingsView from './screens/LocationSettingsView';

const ReportsView: React.FC = () => (
  <div className="space-y-8 pb-16 animate-in fade-in duration-500">
    <div className="section-header">
      <h2 className="ymca-brand-font text-6xl uppercase leading-none">Operational Reports</h2>
      <p className="text-gray-500 font-medium uppercase text-xs tracking-[0.2em] mt-2">Data-driven insights across One YMCA divisions</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="p-10 border-4 border-black bg-ymca-neutral space-y-4">
        <h3 className="ymca-brand-font text-3xl uppercase">Staffing Efficiency</h3>
        <p className="text-sm font-medium text-gray-600">Analyze hours worked vs budget across all locations.</p>
        <button className="btn-primary w-full">Generate Report</button>
      </div>
      <div className="p-10 border-4 border-black bg-ymca-neutral space-y-4">
        <h3 className="ymca-brand-font text-3xl uppercase">Service Impact</h3>
        <p className="text-sm font-medium text-gray-600">Track community engagement and residential support metrics.</p>
        <button className="btn-primary w-full">Generate Report</button>
      </div>
    </div>
  </div>
);

const SettingsView: React.FC = () => (
  <div className="space-y-8 pb-16 animate-in fade-in duration-500">
    <div className="section-header">
      <h2 className="ymca-brand-font text-6xl uppercase leading-none">Global Settings</h2>
      <p className="text-gray-500 font-medium uppercase text-xs tracking-[0.2em] mt-2">Configure workforce rules, locations and AI policies</p>
    </div>
    <div className="bg-white border-2 border-black p-8 space-y-8">
      <div className="space-y-2">
        <h4 className="ymca-brand-font text-2xl uppercase">Organisational Profile</h4>
        <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Manage YMCA branding and site structure</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Organisation Name</label>
          <input type="text" className="form-input" defaultValue="One YMCA" />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Region</label>
          <input type="text" className="form-input" defaultValue="Hertfordshire" />
        </div>
      </div>
      <button className="btn-primary">Save Changes</button>
    </div>
  </div>
);

const MOCK_STAFF: Staff[] = [
  { 
    id: 'exec-1', firstName: 'Guy', lastName: 'Foxell', role: UserRole.ORG_ADMIN, 
    primaryLocationId: 'h-hw', assignedLocationIds: LOCATIONS.map(l => l.id), 
    departments: [DepartmentType.HOUSING, DepartmentType.FAMILY_YOUTH], 
    skills: [{ id: 'ceo', name: 'Strategic Vision', isMandatory: true }], 
    contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' 
  },
  { 
    id: 'exec-2', firstName: 'Michael', lastName: 'Howe', role: UserRole.ORG_ADMIN, 
    primaryLocationId: 'h-hw', assignedLocationIds: LOCATIONS.map(l => l.id), 
    departments: [DepartmentType.HOUSING, DepartmentType.SUPPORT_ADVICE], 
    skills: [{ id: 'dceo', name: 'Operations', isMandatory: true }], 
    contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' 
  },
  { 
    id: 'exec-3', firstName: 'Rob', lastName: 'Clark', role: UserRole.FINANCE, 
    primaryLocationId: 'h-hw', assignedLocationIds: LOCATIONS.map(l => l.id), 
    departments: [DepartmentType.HOUSING, DepartmentType.SUPPORT_ADVICE], 
    skills: [{ id: 'cfo', name: 'Charity Finance', isMandatory: true }], 
    contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' 
  },
  { id: 'ad-1', firstName: 'Linzi', lastName: 'Schier', role: UserRole.ORG_ADMIN, primaryLocationId: 'yp-bs', assignedLocationIds: ['yp-bs', 'yp-ste'], departments: [DepartmentType.FAMILY_YOUTH], skills: [], contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' },
  { id: 'ad-2', firstName: 'Nicola', lastName: 'Lee', role: UserRole.ORG_ADMIN, primaryLocationId: 'h-wel-h', assignedLocationIds: ['h-wel-h', 'h-wel-hmo'], departments: [DepartmentType.SUPPORT_ADVICE, DepartmentType.HOUSING], skills: [], contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' },
  { id: 'ad-3', firstName: 'Steve', lastName: 'Green', role: UserRole.ORG_ADMIN, primaryLocationId: 'h-hw', assignedLocationIds: LOCATIONS.map(l => l.id), departments: [DepartmentType.HOUSING], skills: [{ id: 'it', name: 'ICT Strategy', isMandatory: true }], contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' },
  { id: 'ad-4', firstName: 'Tim', lastName: 'Roberts', role: UserRole.ORG_ADMIN, primaryLocationId: 'h-cha', assignedLocationIds: ['h-cha', 'h-and'], departments: [DepartmentType.SUPPORT_ADVICE], skills: [], contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' },
  { id: 'ad-5', firstName: 'Tom', lastName: 'Cavalot', role: UserRole.ORG_ADMIN, primaryLocationId: 'yp-ste', assignedLocationIds: ['yp-ste', 'yp-bs', 'yp-hod'], departments: [DepartmentType.FAMILY_YOUTH], skills: [{ id: 'yw', name: 'Youth Work Management', isMandatory: true }], contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' },
  { id: 's-hw1', firstName: 'David', lastName: 'Miller', role: UserRole.LOCATION_MANAGER, primaryLocationId: 'h-hw', assignedLocationIds: ['h-hw'], departments: [DepartmentType.HOUSING], skills: [{ id: 'sk1', name: 'Management', isMandatory: true }], contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' },
  { id: 's-hw2', firstName: 'Emma', lastName: 'Wilson', role: UserRole.STAFF, primaryLocationId: 'h-hw', assignedLocationIds: ['h-hw'], departments: [DepartmentType.HOUSING], skills: [{ id: 'sk2', name: 'Safeguarding L3', isMandatory: true }], contractType: 'Full-time', maxWeeklyHours: 37, availability: {}, complianceStatus: 'Active' },
  { id: 's-wel1', firstName: 'James', lastName: 'Taylor', role: UserRole.STAFF, primaryLocationId: 'h-wel-h', assignedLocationIds: ['h-wel-h', 'h-wel-hmo'], departments: [DepartmentType.HOUSING], skills: [{ id: 'sk3', name: 'First Aid', isMandatory: true }], contractType: 'Full-time', maxWeeklyHours: 37, availability: {}, complianceStatus: 'Active' },
  { id: 's-wel2', firstName: 'Sophie', lastName: 'Brown', role: UserRole.STAFF, primaryLocationId: 'h-wel-hmo', assignedLocationIds: ['h-wel-h', 'h-wel-hmo'], departments: [DepartmentType.HOUSING], skills: [{ id: 'sk4', name: 'Housing Support', isMandatory: true }], contractType: 'Part-time', maxWeeklyHours: 20, availability: {}, complianceStatus: 'Warning' },
  { id: 's-ste1', firstName: 'Robert', lastName: 'Davis', role: UserRole.LOCATION_MANAGER, primaryLocationId: 'h-ste-h', assignedLocationIds: ['h-ste-h'], departments: [DepartmentType.HOUSING], skills: [], contractType: 'Full-time', maxWeeklyHours: 40, availability: {}, complianceStatus: 'Active' },
  { id: 's-ste2', firstName: 'Chloe', lastName: 'Anderson', role: UserRole.STAFF, primaryLocationId: 'h-ste-h', assignedLocationIds: ['h-ste-h'], departments: [DepartmentType.HOUSING], skills: [{ id: 'sk1', name: 'Safeguarding L3', isMandatory: true }], contractType: 'Full-time', maxWeeklyHours: 37, availability: {}, complianceStatus: 'Active' },
  { id: 's-she1', firstName: 'Thomas', lastName: 'Moore', role: UserRole.STAFF, primaryLocationId: 'h-she', assignedLocationIds: ['h-she'], departments: [DepartmentType.HOUSING], skills: [{ id: 'sk2', name: 'First Aid', isMandatory: true }], contractType: 'Full-time', maxWeeklyHours: 37, availability: {}, complianceStatus: 'Active' },
  { id: 's-yp1', firstName: 'Liam', lastName: 'Walker', role: UserRole.STAFF, primaryLocationId: 'yp-bs', assignedLocationIds: ['yp-bs'], departments: [DepartmentType.FAMILY_YOUTH], skills: [{ id: 'sk10', name: 'Youth Work L2', isMandatory: true }], contractType: 'Full-time', maxWeeklyHours: 37, availability: {}, complianceStatus: 'Active' },
];

const generateMockShifts = (staff: Staff[]): Shift[] => {
  const shifts: Shift[] = [];
  const startDay = new Date('2024-10-21T00:00:00');
  LOCATIONS.forEach(loc => {
    const assignedStaff = staff.filter(s => s.assignedLocationIds.includes(loc.id));
    if (assignedStaff.length === 0) return;
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      const dayDate = new Date(startDay.getTime() + dayOffset * 86400000);
      const dayStr = dayDate.toISOString().split('T')[0];
      shifts.push({
        id: `shift-${loc.id}-am-${dayOffset}`,
        staffId: assignedStaff[0].id,
        locationId: loc.id,
        department: loc.departments[0],
        startTime: `${dayStr}T09:00:00`,
        endTime: `${dayStr}T17:00:00`,
        roleName: loc.type === 'Housing' ? 'Support Worker' : 'Youth Worker',
        requiredSkills: [],
        status: 'Published'
      });
      if (assignedStaff.length > 1) {
        shifts.push({
          id: `shift-${loc.id}-pm-${dayOffset}`,
          staffId: assignedStaff[1].id,
          locationId: loc.id,
          department: loc.departments[0],
          startTime: `${dayStr}T14:00:00`,
          endTime: `${dayStr}T22:00:00`,
          roleName: loc.type === 'Housing' ? 'Night Support' : 'Session Lead',
          requiredSkills: [],
          status: 'Published'
        });
      }
    }
  });
  return shifts;
};

const App: React.FC = () => {
  const [state, setState] = useState<AppState>({
    currentLocation: LOCATIONS[0].id,
    currentDepartment: DepartmentType.HOUSING,
    selectedDate: new Date('2024-10-21'),
    view: 'overview'
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCascadeModalOpen, setIsCascadeModalOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [staff, setStaff] = useState<Staff[]>(MOCK_STAFF);
  const [shifts, setShifts] = useState<Shift[]>(generateMockShifts(MOCK_STAFF));

  const activeLocation = useMemo(() => 
    LOCATIONS.find(l => l.id === state.currentLocation) || LOCATIONS[0],
    [state.currentLocation]
  );

  const handleNav = (view: AppState['view'], locationId?: string) => {
    setState(prev => ({ 
      ...prev, 
      view,
      currentLocation: locationId || prev.currentLocation
    }));
    setIsSidebarOpen(false);
    window.scrollTo(0, 0);
  };

  const renderContent = () => {
    switch (state.view) {
      case 'overview':
        return <Dashboard state={state} staff={staff} shifts={shifts} onNavigate={handleNav} />;
      case 'rota':
        return <RotaPlanner state={state} staff={staff} shifts={shifts} setShifts={setShifts} onFullScreenToggle={() => setIsFullScreen(!isFullScreen)} isFullScreen={isFullScreen} />;
      case 'people':
        return <PeopleView staff={staff} onUpdateStaff={setStaff} />;
      case 'locations':
        return <LocationSettingsView staff={staff} setStaff={setStaff} />;
      case 'jobs':
        return <JobsView />;
      case 'forms':
        return <FormsView />;
      case 'leave':
        return <LeaveView staff={staff} />;
      case 'timesheets':
        return <TimesheetView staff={staff} shifts={shifts} />;
      case 'files':
        return <FileStorageView />;
      case 'warmSpaces':
        return <WarmSpacesView />;
      case 'reports':
        return <ReportsView />;
      case 'settings':
        return <SettingsView />;
      default:
        return <Dashboard state={state} staff={staff} shifts={shifts} onNavigate={handleNav} />;
    }
  };

  return (
    <div className={`flex h-screen overflow-hidden bg-white transition-all duration-300 ${isFullScreen ? 'fullscreen-mode' : ''}`}>
      {!isFullScreen && (
        <div 
          className={`fixed inset-0 z-50 transition-all duration-300 lg:relative lg:translate-x-0 ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } ${isSidebarCollapsed ? 'lg:w-20' : 'lg:w-72'}`}
        >
          {isSidebarOpen && (
            <div className="fixed inset-0 bg-black/60 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
          )}
          <div className="h-full relative z-10 w-full overflow-hidden border-r border-black/10">
            <Sidebar 
              currentView={state.view} 
              onNavigate={handleNav}
              onClose={() => setIsSidebarOpen(false)}
              onConnectCascade={() => setIsCascadeModalOpen(true)}
              isCollapsed={isSidebarCollapsed}
              onCollapseToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 h-full relative overflow-hidden">
        {!isFullScreen && (
          <Header 
            state={state} 
            setState={setState} 
            location={activeLocation}
            onMenuClick={() => setIsSidebarOpen(true)}
          />
        )}
        <main className={`flex-1 overflow-y-auto ${isFullScreen ? 'p-0' : 'p-4 md:p-8'} transition-all duration-500`}>
          <div className={`${isFullScreen ? 'w-full h-full' : 'max-w-screen-2xl mx-auto w-full'}`}>
            {renderContent()}
          </div>
        </main>
      </div>

      {isCascadeModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm p-8 border-4 border-black animate-in zoom-in duration-200">
            <button 
              onClick={() => setIsCascadeModalOpen(false)}
              className="absolute top-4 right-4 text-black hover:text-ymca-orange text-2xl font-bold"
            >
              &times;
            </button>
            <div className="space-y-6 text-center md:text-left">
              <div className="space-y-1">
                <span className="text-ymca-orange font-bold text-[9px] uppercase tracking-[0.25em]">External Integration</span>
                <h2 className="ymca-brand-font text-5xl leading-none uppercase text-black">CASCADE IRIS API</h2>
                <p className="text-gray-500 text-[11px] font-medium leading-relaxed mt-2">Sync One YMCA workforce data with IRIS Cascade.</p>
              </div>
              <div className="space-y-4">
                <input type="password" placeholder="Iris API Key..." className="w-full border-2 border-black p-3.5 text-xs font-bold" />
                <input type="text" placeholder="Client ID" className="w-full border-2 border-black p-3.5 text-xs font-bold" />
              </div>
              <div className="flex flex-col md:flex-row gap-3 pt-2">
                <button onClick={() => setIsCascadeModalOpen(false)} className="flex-1 py-3.5 border-2 border-black font-black text-[10px] tracking-widest uppercase">Cancel</button>
                <button onClick={() => setIsCascadeModalOpen(false)} className="flex-1 py-3.5 bg-black text-white font-black text-[10px] tracking-widest uppercase">Authorise</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
